import {trackRemoveFromCart} from '@amblique/king-living/app/analytics/ga4/events';
import {
  buildGa4ChildBasketLineItem,
  buildGa4PrimaryCoverBasketLineItem,
  collectGa4BasketProductIds,
  resolveGa4CategoryProduct,
  resolveGa4MasterProduct,
} from '@amblique/king-living/app/analytics/ga4/basket-ga4';
import {fetchGa4OptionProductMeta} from '@amblique/king-living/app/analytics/ga4/customize-atc';
import {resolveKingGuardGa4Item} from '@amblique/king-living/app/analytics/ga4/king-guard';
import {resolveBasketRemovalGroup} from '@amblique/king-living/app/utils/cart-utils';

/**
 * Map a basket configuration group to GA4 `remove_from_cart` items — mirrors
 * customize `add_to_cart`: primary cover variant + child option lines + KingGuard.
 */
const buildGa4RemoveFromCartItemsFromBasketGroup = ({
  masterProduct = null,
  parentLine,
  childLines = [],
  currency,
  imageMap = {},
  nameMap = {},
  productsById = {},
}) => {
  if (!parentLine?.productId) {
    return [];
  }

  const categoryProduct = resolveGa4CategoryProduct({
    lineProductId: parentLine.productId,
    masterProduct,
    productsById,
  });
  const primaryGa4Item = buildGa4PrimaryCoverBasketLineItem({
    categoryProduct,
    currency,
    imageMap,
    line: parentLine,
    masterProduct,
    productsById,
  });
  const items = [];

  if (primaryGa4Item) {
    items.push(primaryGa4Item);
  }

  childLines.forEach((childLine) => {
    const childGa4Item = buildGa4ChildBasketLineItem({
      categoryProduct: productsById[childLine.productId] || masterProduct,
      currency,
      imageMap,
      line: childLine,
      nameMap,
      productsById,
    });

    if (childGa4Item) {
      items.push(childGa4Item);
    }
  });

  const kingGuardItem = resolveKingGuardGa4Item({
    categoryProduct,
    lineItem: parentLine,
    masterProduct,
    productsById,
    useBasketPrice: true,
  });

  return kingGuardItem ? [...items, kingGuardItem] : items;
};

/**
 * Cart flyout / checkout order summary remove — one visible group → multiple GA4 items.
 */
export const trackRemoveFromCartFromBasketLine = async ({
  api,
  currency,
  customer,
  getToken,
  lineItem,
  productItems = [],
  productsById: preloadedProductsById = null,
  queryClient = null,
}) => {
  const {parent, children} = resolveBasketRemovalGroup(lineItem, productItems);
  const rawParent = productItems.find((li) => li.itemId === parent.itemId) ?? parent;
  const productIds = collectGa4BasketProductIds([rawParent, ...children]);
  const {imageMap, nameMap, productsById} = await fetchGa4OptionProductMeta({
    api,
    getToken,
    includePrices: false,
    productIds,
    productsById: preloadedProductsById,
    queryClient,
    useBasketProductParams: true,
  });
  const masterProduct = resolveGa4MasterProduct(rawParent, productsById, {fallbackToLineProduct: true});
  const items = buildGa4RemoveFromCartItemsFromBasketGroup({
    childLines: children,
    currency,
    imageMap,
    masterProduct,
    nameMap,
    parentLine: rawParent,
    productsById,
  });

  if (!items.length) {
    return false;
  }

  return trackRemoveFromCart({currency, customer, items});
};
